import { Button } from '@mui/material';
import FindingsList from './FindingsList';
import { listAllFindings } from '../../../services/securityhubApi';
import { detectiveEngine } from './engines/detective';

// Amazon Detective investigations, rendered through the shared findings list.
//
// THE SOURCE IS THE UNIFIED INBOX SCOPED TO THIS ENGINE, not detectiveApi's
// listDetectiveFindings. /findings/all returns the NormalizedFinding shape the
// shared table renders (findingId / severity / workflowStatus / recordState /
// lastObservedAt) and tags every row engine='detective', which is what makes a row
// click open the Detective detail body. listDetectiveFindings returns Detective's
// OWN DTO — investigationId / state / entityArn — whose field names the table does
// not read.
//
// The filter vocabulary differs for the same reason and it is not a contradiction:
// spec §5.7's `state[]` (ACTIVE|ARCHIVED) is a filter on Detective's own DB-side
// endpoint, while this page reaches the identical column through the normalized
// `workflowStatus[]` (NEW|RESOLVED), which the engine adapter maps back with
// detectiveStateFromWorkflow. Two entry points, one filter; listDetectiveFindings
// stays exported (one export per route) and stays unused by this page, deliberately.
const detectiveSource = (filters) => listAllFindings({ ...filters, engine: ['detective'] });

// Accent and icon come from the engine descriptor rather than local constants so
// this page cannot drift from the sidebar entry and the Overview tile.
const DetectiveIcon = detectiveEngine.Icon;

const DETECTIVE_CONSOLE_URL = 'https://console.aws.amazon.com/detective/';

// The same explanation the dashboard's empty state carries, because an operator
// can land on either screen first.
//
// Zero investigations is Detective's EXPECTED steady state, not a fault: Detective
// creates no investigation records on its own — it recommends entities worth
// investigating, and a report exists only once somebody runs an investigation from
// the AWS console (or calls StartInvestigation, which Prism deliberately never
// does). The shared list's default copy — "Findings appear here as soon as your
// enrolled accounts report them" — is true for the other four engines and is a
// quiet lie here: nothing will ever arrive on its own, and an operator reading it
// concludes the engine is broken.
//
// The account and region counts the dashboard names are deliberately absent: they
// would cost this page a second endpoint (getDetectiveSummary) for one sentence,
// and the dashboard is where coverage is stated.
const detectiveEmptyState = {
  title: 'No investigations yet',
  description:
    'Amazon Detective does not create investigations on its own — it recommends entities worth '
    + 'investigating, and a report exists only once someone runs an investigation. Prism reads '
    + 'those reports; it does not start them. Run an investigation from the Amazon Detective '
    + 'console and it will appear here after the next refresh.',
  action: (
    <Button
      variant="outlined"
      size="small"
      href={DETECTIVE_CONSOLE_URL}
      target="_blank"
      rel="noreferrer"
    >
      Open the Detective console
    </Button>
  ),
};

export default function DetectiveFindings() {
  return (
    <FindingsList
      source={detectiveSource}
      engineKind="detective"
      subtitle="Detective investigations"
      color={detectiveEngine.color}
      icon={<DetectiveIcon sx={{ fontSize: 24 }} />}
      emptyState={detectiveEmptyState}
    />
  );
}
